import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi, downloadBlob } from '@/lib/api';
import { 
  Search, 
  Eye, 
  Loader2,
  ShoppingCart,
  Filter,
  ExternalLink,
  FileText,
  Check,
  ChevronsUpDown,
  Phone,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    packed: 'bg-blue-100 text-blue-800',
    on_the_way: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    delayed: 'bg-orange-100 text-orange-800',
    rescheduled: 'bg-rose-100 text-rose-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const getStatusLabel = (status) => {
  const labels = {
    pending: 'Pending',
    packed: 'Packed',
    on_the_way: 'In Transit',
    delivered: 'Delivered',
    delayed: 'Delayed',
    rescheduled: 'Rescheduled'
  };
  return labels[status] || status.replace('_', ' ');
};

const getMilestoneLabel = (status) => {
  const labels = {
    pending: 'Order Created',
    packed: 'Packed',
    on_the_way: 'In Transit',
    delivered: 'Delivered'
  };
  return labels[status] || getStatusLabel(status);
};

const getLatestStatusEntry = (history, status) => {
  if (!Array.isArray(history)) return null;
  const entries = history.filter((item) => item?.status === status);
  if (entries.length === 0) return null;
  return entries
    .slice()
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .at(-1);
};

export default function OrdersPage() {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [downloadingBOS, setDownloadingBOS] = useState(false);
  const [generatingGst, setGeneratingGst] = useState(false);
  const [downloadingGst, setDownloadingGst] = useState(false);
  const [bulkDownloadingBOS, setBulkDownloadingBOS] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [showBosDownloadModal, setShowBosDownloadModal] = useState(false);
  const [bosDownloadOrder, setBosDownloadOrder] = useState(null);
  const [bosRenderMode, setBosRenderMode] = useState('customer');
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersOptions, setCustomersOptions] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerPricing, setCustomerPricing] = useState([]);
  const [orderItems, setOrderItems] = useState([{ variant_id: '', pieces: '1' }]);
  const [itemVariantSelectorOpen, setItemVariantSelectorOpen] = useState({});
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [showStatusReasonModal, setShowStatusReasonModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAction, setPaymentAction] = useState('deliver');
  const [paymentOption, setPaymentOption] = useState('no_payment');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentCollectionSource, setPaymentCollectionSource] = useState('direct_company');
  const [paymentCollectedByStaffId, setPaymentCollectedByStaffId] = useState('none');
  const [paymentCustomerReference, setPaymentCustomerReference] = useState('');
  const [paymentCustomerCollectionNotes, setPaymentCustomerCollectionNotes] = useState('');
  const [paymentRemarks, setPaymentRemarks] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState(['cash', 'upi', 'bank', 'cheque']);
  const [customerOutstanding, setCustomerOutstanding] = useState(0);
  const [redeemableCreditNotes, setRedeemableCreditNotes] = useState([]);
  const [selectedCreditNoteId, setSelectedCreditNoteId] = useState('none');
  const [voucherLoadingId, setVoucherLoadingId] = useState('');
  const [showCustomerDetailModal, setShowCustomerDetailModal] = useState(false);
  const [customerDetailLoading, setCustomerDetailLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [statusErrorItems, setStatusErrorItems] = useState([]);
  const [salesStockByVariant, setSalesStockByVariant] = useState({});
  const [salesWarehouseName, setSalesWarehouseName] = useState('Sales Warehouse');

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setStatusFilter(statusParam);
    }

    const createOrderParam = searchParams.get('create_order');
    const customerIdParam = searchParams.get('customer_id');
    if (createOrderParam === '1') {
      setShowCreateModal(true);
      loadCustomersForCreate();
      if (customerIdParam) {
        handleCustomerSelect(customerIdParam);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, staffFilter]);

  useEffect(() => {
    fetchData();
  }, [search, statusFilter, staffFilter, page]);

  useEffect(() => {
    setSelectedOrderIds([]);
  }, [orders]);

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const extractFilename = (headers, fallback) => {
    const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    const match = disposition?.match(/filename=\"?([^"]+)\"?/i);
    return match?.[1] || fallback;
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('orders', {
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        staff_id: staffFilter !== 'all' ? staffFilter : undefined,
      });
      downloadBlob(res.data, 'orders.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const fetchData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (staffFilter !== 'all') params.sales_staff_id = staffFilter;
      
      params.page = page;
      params.page_size = pageSize;

      const [ordersRes, staffRes] = await Promise.all([
        adminApi.getOrders(params),
        adminApi.getSalesStaff()
      ]);
      
      setOrders(ordersRes.data.items || []);
      setTotal(ordersRes.data.total || 0);
      setStaff(staffRes.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (order) => {
    setDetailLoading(true);
    setShowDetailModal(true);
    setStatusErrorItems([]);
    try {
      const response = await adminApi.getOrderById(order.id);
      setSelectedOrder(response.data);
      try {
        const customerResponse = await adminApi.getCustomerById(response.data.customer_id);
        setCustomerOutstanding(customerResponse.data?.outstanding_amount || 0);
      } catch (error) {
        setCustomerOutstanding(0);
      }
    } catch (error) {
      toast.error('Failed to fetch order details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewCustomerDetails = async (customerId) => {
    if (!customerId) return;
    setCustomerDetailLoading(true);
    setShowCustomerDetailModal(true);
    try {
      const response = await adminApi.getCustomerById(customerId);
      setSelectedCustomer(response.data);
    } catch (error) {
      toast.error('Failed to fetch customer details');
      setShowCustomerDetailModal(false);
    } finally {
      setCustomerDetailLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, reason) => {
    if (selectedOrder?.status === 'delivered') {
      toast.error('Delivered orders cannot be updated');
      return;
    }
    setUpdatingStatus(true);
    try {
      setStatusErrorItems([]);
      await adminApi.updateOrderStatus(orderId, { status: newStatus, reason });
      toast.success(`Order status updated to ${newStatus}`);
      
      // Update local state
      if (selectedOrder) {
        const historyEntry = {
          status: newStatus,
          date: new Date().toISOString(),
          remarks: reason || null,
        };
        const history = Array.isArray(selectedOrder.status_history)
          ? [...selectedOrder.status_history, historyEntry]
          : [historyEntry];
        setSelectedOrder({ ...selectedOrder, status: newStatus, status_history: history });
      }
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'LOW_STOCK') {
        const items = Array.isArray(detail.items) ? detail.items : [];
        setStatusErrorItems(items);
        toast.error(detail.message || 'Some items are out of stock in Sales Warehouse. Please update stock before packing.');
      } else if (detail && typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to update status');
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleStatusClick = (orderId, newStatus) => {
    if (selectedOrder?.status === 'delivered') {
      toast.error('Delivered orders cannot be updated');
      return;
    }
    if (newStatus === 'delivered') {
      if (selectedOrder?.status !== 'packed') {
        toast.error('Only packed orders can be marked as delivered');
        return;
      }
      openPaymentModal('deliver');
      return;
    }
    if (['delayed', 'rescheduled'].includes(newStatus)) {
      setPendingStatus(newStatus);
      setStatusReason('');
      setShowStatusReasonModal(true);
      return;
    }
    handleStatusUpdate(orderId, newStatus);
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await adminApi.getSettings();
      const methods = response.data?.payment_methods;
      if (Array.isArray(methods) && methods.length > 0) {
        setPaymentMethods(methods);
        if (!methods.includes(paymentMethod)) {
          setPaymentMethod(methods[0]);
        }
      }
    } catch (error) {
      setPaymentMethods(['cash', 'upi', 'bank', 'cheque']);
    }
  };

  const openPaymentModal = async (actionType) => {
    if (!selectedOrder) return;
    setPaymentAction(actionType);
    setPaymentOption(actionType === 'deliver' ? 'no_payment' : 'partial');
    setPaymentAmount('');
    setPaymentCollectionSource('direct_company');
    setPaymentCollectedByStaffId(selectedOrder.sales_staff_id || 'none');
    setPaymentCustomerReference('');
    setPaymentCustomerCollectionNotes('');
    setPaymentRemarks('');
    setSelectedCreditNoteId('none');
    setRedeemableCreditNotes([]);
    setShowPaymentModal(true);
    loadPaymentMethods();
    try {
      const [customerResponse, creditNotesResponse] = await Promise.all([
        adminApi.getCustomerById(selectedOrder.customer_id),
        adminApi.getRedeemableCreditNotes(selectedOrder.customer_id),
      ]);
      setCustomerOutstanding(customerResponse.data?.outstanding_amount || 0);
      setRedeemableCreditNotes(creditNotesResponse.data || []);
    } catch (error) {
      setCustomerOutstanding(0);
      setRedeemableCreditNotes([]);
    }
  };

  const resolvePaymentAmount = () => {
    if (!selectedOrder) return 0;
    if (paymentOption === 'full') {
      return customerOutstanding || selectedOrder.balance_amount || 0;
    }
    if (paymentOption === 'current') {
      return selectedOrder.total_amount || 0;
    }
    if (paymentOption === 'partial') {
      return Number(paymentAmount) || 0;
    }
    return 0;
  };

  const handlePaymentOptionChange = (value) => {
    setPaymentOption(value);
    if (value === 'full') {
      setPaymentAmount(String(customerOutstanding || selectedOrder?.balance_amount || 0));
    } else if (value === 'current') {
      setPaymentAmount(String(selectedOrder?.total_amount || 0));
    } else if (value === 'no_payment') {
      setPaymentAmount('');
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedOrder) return;
    if (paymentAction === 'add' && paymentOption === 'no_payment') {
      toast.error('Please select a payment option');
      return;
    }

    const amount = resolvePaymentAmount();
    const selectedCreditNote = redeemableCreditNotes.find((note) => note.id === selectedCreditNoteId);
    const creditNoteAmount = Number(selectedCreditNote?.amount || 0);
    if (paymentOption !== 'no_payment' && amount <= 0 && creditNoteAmount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    if (paymentCollectionSource === 'staff_collected' && paymentCollectedByStaffId === 'none') {
      toast.error('Please select the staff who collected the payment');
      return;
    }

    setPaymentProcessing(true);
    try {
      if (paymentOption !== 'no_payment') {
        await adminApi.createPayment({
          customer_id: selectedOrder.customer_id,
          order_id: selectedOrder.id,
          amount,
          auto_allocate: paymentOption === 'full',
          payment_method: paymentMethod,
          collection_source: paymentCollectionSource,
          collected_by_staff_id: paymentCollectionSource === 'staff_collected' && paymentCollectedByStaffId !== 'none' ? paymentCollectedByStaffId : undefined,
          customer_paid_mode: paymentMethod,
          customer_collection_reference: paymentCustomerReference || undefined,
          customer_collection_notes: paymentCustomerCollectionNotes || undefined,
          credit_note_id: selectedCreditNote?.id,
          notes: paymentRemarks || '',
        });
      }

      if (paymentAction === 'deliver') {
        await handleStatusUpdate(selectedOrder.id, 'delivered');
      }

      const refreshed = await adminApi.getOrderById(selectedOrder.id);
      setSelectedOrder(refreshed.data);
      setShowPaymentModal(false);
      toast.success(paymentOption === 'no_payment' ? 'Order delivered' : 'Settlement recorded');
    } catch (error) {
      const message = error?.response?.data?.detail || 'Failed to record payment';
      toast.error(message);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleConfirmStatusReason = () => {
    if (!selectedOrder) return;
    if (!statusReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    setShowStatusReasonModal(false);
    handleStatusUpdate(selectedOrder.id, pendingStatus, statusReason.trim());
  };

  const handleViewVoucherPdf = async (payment) => {
    if (!payment?.voucher_id) {
      toast.error('No voucher linked to this payment');
      return;
    }
    setVoucherLoadingId(payment.id);
    try {
      const response = await adminApi.getVoucherPdfUrl(payment.voucher_id);
      const url = response?.data?.voucher_pdf_url;
      if (!url) throw new Error('Voucher PDF URL unavailable');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to open voucher PDF');
    } finally {
      setVoucherLoadingId('');
    }
  };

  const loadCustomersForCreate = async () => {
    setCustomersLoading(true);
    try {
      const response = await adminApi.getCustomers({ page: 1, page_size: 1000 });
      setCustomersOptions(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    loadCustomersForCreate();
  };

  const handleCustomerSelect = async (customerId) => {
    setSelectedCustomerId(customerId);
    if (!customerId) {
      setCustomerPricing([]);
      setSalesStockByVariant({});
      setOrderItems([{ variant_id: '', pieces: '1' }]);
      return;
    }
    try {
      const response = await adminApi.getCustomerById(customerId);
      const pricing = response.data?.pricing || [];
      setCustomerPricing(pricing);
      const variantIds = pricing.map((p) => p.variant_id).filter(Boolean);
      await loadSalesWarehouseStock(variantIds);
      setOrderItems([{ variant_id: '', pieces: '1' }]);
    } catch (error) {
      toast.error('Failed to load customer pricing');
    }
  };

  const loadSalesWarehouseStock = async (variantIds = []) => {
    try {
      const warehousesRes = await adminApi.getWarehouses();
      const warehouses = warehousesRes.data || [];
      const salesWarehouse =
        warehouses.find((w) => w.is_default_sales) ||
        warehouses.find((w) => (w.name || '').toLowerCase() === 'sales warehouse') ||
        null;

      if (!salesWarehouse?.id) {
        setSalesStockByVariant({});
        return;
      }

      setSalesWarehouseName(salesWarehouse.name || 'Sales Warehouse');
      const stockRes = await adminApi.getWarehouseStock(salesWarehouse.id);
      const rows = stockRes.data || [];
      const variantFilter = new Set(variantIds);
      const map = {};

      rows.forEach((row) => {
        const variantId = row?.variant_id;
        if (!variantId) return;
        if (variantFilter.size > 0 && !variantFilter.has(variantId)) return;
        const fallbackAvailable = Number(row?.on_hand || 0) - Number(row?.reserved || 0);
        const resolvedAvailable = row?.available ?? fallbackAvailable;
        map[variantId] = Number(resolvedAvailable || 0);
      });

      setSalesStockByVariant(map);
    } catch (error) {
      setSalesStockByVariant({});
    }
  };

  const getAvailableStock = (variantId) => {
    return Number(salesStockByVariant?.[variantId] ?? 0);
  };

  const handleAddOrderItem = () => {
    setOrderItems((prev) => [...prev, { variant_id: '', pieces: '1' }]);
  };

  const handleRemoveOrderItem = (index) => {
    setOrderItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    setOrderItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const getVariantOptionLabel = (variant) => {
    return `${variant.master_name} · ${variant.pack_size_label} (₹${variant.effective_price})`;
  };

  const getSelectedVariantLabel = (variantId) => {
    const selected = customerPricing.find((variant) => variant.variant_id === variantId);
    return selected ? getVariantOptionLabel(selected) : 'Select product';
  };

  const getVariantPrice = (variantId) => {
    const variant = customerPricing.find((item) => item.variant_id === variantId);
    return variant?.effective_price ?? 0;
  };

  const calculateOrderTotal = () => {
    return orderItems.reduce((sum, item) => {
      const pieces = Number(item.pieces) || 0;
      const price = getVariantPrice(item.variant_id);
      return sum + price * pieces;
    }, 0);
  };

  const handleCreateOrder = async () => {
    if (!selectedCustomerId) {
      toast.error('Please select a customer');
      return;
    }
    const invalidItem = orderItems.some((item) => !item.variant_id || Number(item.pieces) <= 0);
    if (invalidItem) {
      toast.error('Please add valid items and quantities');
      return;
    }

    setCreatingOrder(true);
    try {
      const totalAmount = calculateOrderTotal();
      const payload = {
        customer_id: selectedCustomerId,
        items: orderItems.map((item) => ({
          variant_id: item.variant_id,
          pieces: Number(item.pieces),
        })),
        total_amount: totalAmount,
        channel: 'customer_executive',
        expected_delivery_date: expectedDeliveryDate || undefined,
        notes: orderNotes || '',
      };
      await adminApi.createOrder(payload);
      toast.success('Order created successfully');
      setShowCreateModal(false);
      setSelectedCustomerId('');
      setCustomerPricing([]);
      setSalesStockByVariant({});
      setOrderItems([{ variant_id: '', pieces: '1' }]);
      setExpectedDeliveryDate('');
      setOrderNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to create order');
    } finally {
      setCreatingOrder(false);
    }
  };

  const openBosDownloadModal = (order) => {
    setBosDownloadOrder(order);
    setBosRenderMode('customer');
    setShowBosDownloadModal(true);
  };

  const handleDownloadBill = async () => {
    if (!bosDownloadOrder) return;
    setDownloadingBOS(true);
    try {
      const response = await adminApi.downloadBillOfSupplyFile(bosDownloadOrder.id, { render_mode: bosRenderMode });
      const filename = extractFilename(response.headers, `${bosDownloadOrder.bill_of_supply_no || bosDownloadOrder.order_id || 'bill-of-supply'}.pdf`);
      triggerBlobDownload(response.data, filename);
      setShowBosDownloadModal(false);
    } catch (error) {
      toast.error('Failed to generate bill of supply');
    } finally {
      setDownloadingBOS(false);
    }
  };

  const handleGenerateGstInvoice = async (order = selectedOrder) => {
    if (!order?.id) return;
    setGeneratingGst(true);
    try {
      const response = await adminApi.generateGstInvoice(order.id);
      toast.success('GST invoice generated');
      const refreshed = await adminApi.getOrderById(order.id);
      setSelectedOrder(refreshed.data);
      fetchData();
      if (response.data?.download_url) {
        window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate GST invoice');
    } finally {
      setGeneratingGst(false);
    }
  };

  const handleDownloadGstInvoice = async (order = selectedOrder) => {
    if (!order?.id) return;
    setDownloadingGst(true);
    try {
      const response = await adminApi.downloadGstInvoiceFile(order.id);
      triggerBlobDownload(response.data, `${order.gst_invoice_no || order.order_id || 'gst-invoice'}.pdf`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download GST invoice');
    } finally {
      setDownloadingGst(false);
    }
  };

  const toggleOrderSelection = (orderId, checked) => {
    setSelectedOrderIds((prev) => {
      if (checked) {
        return prev.includes(orderId) ? prev : [...prev, orderId];
      }
      return prev.filter((id) => id !== orderId);
    });
  };

  const visibleOrderIds = orders.map((order) => order.id);
  const allVisibleSelected = visibleOrderIds.length > 0 && visibleOrderIds.every((id) => selectedOrderIds.includes(id));

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrderIds(visibleOrderIds);
      return;
    }
    setSelectedOrderIds([]);
  };

  const handleBulkDownloadBills = async () => {
    if (selectedOrderIds.length === 0) {
      toast.error('Select at least one order');
      return;
    }
    setBulkDownloadingBOS(true);
    try {
      const response = await adminApi.bulkDownloadBillOfSupply(selectedOrderIds);
      const filename = extractFilename(response.headers, 'bill-of-supply-bulk.zip');
      triggerBlobDownload(response.data, filename);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download bill of supply files');
    } finally {
      setBulkDownloadingBOS(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <LoadingOverlay
        open={downloadingBOS || bulkDownloadingBOS || downloadingExcel || generatingGst || downloadingGst}
        message={generatingGst ? 'Generating GST invoice...' : downloadingGst ? 'Preparing GST invoice...' : downloadingExcel ? 'Please wait. Report is being generated' : bulkDownloadingBOS ? 'Preparing BOS ZIP...' : 'Preparing Bill of Supply...'}
      />
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Orders</h1>
        <p className="text-[#A8A8A8] mt-1">View and manage all orders</p>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleDownloadExcel}
          className="border-[#E5E5E5]"
        >
          Download Excel
        </Button>
        <Button
          variant="outline"
          onClick={handleBulkDownloadBills}
          disabled={selectedOrderIds.length === 0 || bulkDownloadingBOS}
          className="border-[#E5E5E5]"
        >
          Download Selected BOS
        </Button>
        <Button
          onClick={handleOpenCreateModal}
          className="bg-[#C97A5E] hover:bg-[#B06850]"
          data-testid="create-order-btn"
        >
          Create Order
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A8A8]" />
              <Input
                placeholder="Search by order ID or customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full lg:w-40 border-[#E5E5E5]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="packed">Packed</SelectItem>
                <SelectItem value="on_the_way">On The Way</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="delayed">Delayed</SelectItem>
                <SelectItem value="rescheduled">Rescheduled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-full lg:w-48 border-[#E5E5E5]" data-testid="staff-filter">
                <SelectValue placeholder="Sales Staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
              <ShoppingCart className="w-12 h-12 mb-4" />
              <p>No orders found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">
                      <Checkbox checked={allVisibleSelected} onCheckedChange={handleToggleSelectAll} />
                    </th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Order ID</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Date</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Customer</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Sales Staff</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Total</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Paid</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Balance</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Status</th>
                    <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-[#FAFAF9]/50 transition-colors" data-testid={`order-row-${o.id}`}>
                      <td className="py-4 px-6">
                        <Checkbox
                          checked={selectedOrderIds.includes(o.id)}
                          onCheckedChange={(checked) => toggleOrderSelection(o.id, Boolean(checked))}
                        />
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-medium text-[#2D2420]">{o.order_id}</p>
                          {o.bill_of_supply_no ? (
                            <p className="text-xs text-[#A8A8A8]">BOS: {o.bill_of_supply_no}</p>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-[#A8A8A8]">{formatDate(o.created_at)}</td>
                      <td className="py-4 px-6">
                        <div>
                          <button
                            type="button"
                            onClick={() => handleViewCustomerDetails(o.customer_id)}
                            className="font-medium text-[#2D2420] hover:text-[#C97A5E] hover:underline text-left"
                            data-testid={`view-customer-from-order-${o.id}`}
                          >
                            {o.customer_name}
                          </button>
                          <p className="text-sm text-[#A8A8A8]">{o.customer_shop}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-[#2D2420]">{o.sales_staff_name}</td>
                      <td className="py-4 px-6 font-semibold text-[#2D2420]">{formatCurrency(o.total_amount)}</td>
                      <td className="py-4 px-6 text-[#4A7c59]">{formatCurrency(o.paid_amount)}</td>
                      <td className="py-4 px-6">
                        <span className={cn(
                          "font-semibold",
                          o.balance_amount > 0 ? "text-[#E76F51]" : "text-[#4A7c59]"
                        )}>
                          {formatCurrency(o.balance_amount)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={cn("text-xs", getStatusColor(o.status))}>
                          {o.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openBosDownloadModal(o)}
                            disabled={downloadingBOS}
                            className="text-[#5C4033] border-[#E5E5E5] hover:bg-[#FAFAF9]"
                            data-testid={`download-bos-${o.id}`}
                          >
                            Dowload BOS
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => (o.gst_invoice_no ? handleDownloadGstInvoice(o) : handleGenerateGstInvoice(o))}
                            disabled={generatingGst || downloadingGst}
                            className="text-[#5C4033] border-[#E5E5E5] hover:bg-[#FAFAF9]"
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            {o.gst_invoice_no ? 'GST PDF' : 'GST Invoice'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(o)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            data-testid={`view-order-${o.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {!loading && total > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-[#E5E5E5]">
            <p className="text-sm text-[#A8A8A8]">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.max(1, prev - 1));
                    }}
                    className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-[#5C4033]">
                    Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1));
                    }}
                    className={page >= Math.max(1, Math.ceil(total / pageSize)) ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Order Details</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : selectedOrder && (
            <div className="space-y-6 py-4">
              {/* Order Summary */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-[#2D2420]">{selectedOrder.order_id}</h3>
                  <p className="text-[#A8A8A8]">{formatDate(selectedOrder.created_at)}</p>
                  {selectedOrder.bill_of_supply_no ? (
                    <p className="text-sm text-[#5C4033]">BOS: {selectedOrder.bill_of_supply_no}</p>
                  ) : null}
                  {selectedOrder.gst_invoice_no ? (
                    <p className="text-sm text-[#5C4033]">GST Invoice: {selectedOrder.gst_invoice_no}</p>
                  ) : null}
                </div>
                <Badge className={cn("text-sm", getStatusColor(selectedOrder.status))}>
                  {selectedOrder.status.replace('_', ' ')}
                </Badge>
              </div>

              {/* Customer & Staff Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#FAFAF9] rounded-xl">
                  <p className="text-xs text-[#A8A8A8] mb-1">Customer</p>
                  <p className="font-medium text-[#2D2420]">{selectedOrder.customer_name}</p>
                  <p className="text-sm text-[#5C4033]">{selectedOrder.customer_shop}</p>
                </div>
                <div className="p-4 bg-[#FAFAF9] rounded-xl">
                  <p className="text-xs text-[#A8A8A8] mb-1">Sales Staff</p>
                  <p className="font-medium text-[#2D2420]">{selectedOrder.sales_staff_name}</p>
                  <p className="text-sm text-[#5C4033]">via {selectedOrder.channel}</p>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <div className="mb-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF9] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-[#2D2420]">Order Status History</h4>
                    <Badge className={cn("text-xs", getStatusColor(selectedOrder.status))}>
                      {getStatusLabel(selectedOrder.status)}
                    </Badge>
                  </div>
                  <TooltipProvider>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['pending', 'packed', 'on_the_way', 'delivered'].map((status) => {
                        const entry = getLatestStatusEntry(selectedOrder.status_history, status);
                        return (
                          <Tooltip key={status}>
                            <TooltipTrigger asChild>
                              <div className="flex items-start gap-3 cursor-default">
                                <div
                                  className={cn(
                                    'mt-1 h-3 w-3 rounded-full',
                                    entry ? 'bg-[#C97A5E]' : 'bg-[#E5E5E5]'
                                  )}
                                />
                                <div>
                                  <p className="text-xs font-semibold text-[#2D2420]">{getMilestoneLabel(status)}</p>
                                  <p className="text-[11px] text-[#A8A8A8]">
                                    {entry?.date ? formatDateTime(entry.date) : '—'}
                                  </p>
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {entry?.remarks ? entry.remarks : 'No remarks'}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-3">
                      {['delayed', 'rescheduled'].map((status) => {
                        const entry = getLatestStatusEntry(selectedOrder.status_history, status);
                        return (
                          <Tooltip key={status}>
                            <TooltipTrigger asChild>
                              <div className={cn(
                                'flex items-center gap-2 rounded-full px-3 py-1 text-xs border',
                                entry ? 'border-orange-200 bg-orange-50 text-orange-800' : 'border-[#E5E5E5] text-[#A8A8A8]'
                              )}>
                                <span className={cn('h-2 w-2 rounded-full', entry ? 'bg-orange-400' : 'bg-[#E5E5E5]')} />
                                {getStatusLabel(status)}
                                <span className="text-[10px]">
                                  {entry?.date ? formatDate(entry.date) : '—'}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {entry?.remarks ? entry.remarks : 'No remarks'}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </TooltipProvider>
                </div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Order Items</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                      <div>
                        <p className="font-medium text-[#2D2420]">{item.product_name}</p>
                        <p className="text-sm text-[#A8A8A8]">{item.quantity_type} × {item.pieces}</p>
                        {statusErrorItems.find((s) => s.variant_id === item.variant_id) && (
                          <p className="text-xs text-[#E76F51] mt-1">
                            Out of stock in Sales Warehouse. Required {statusErrorItems.find((s) => s.variant_id === item.variant_id)?.required}, available {statusErrorItems.find((s) => s.variant_id === item.variant_id)?.available}.
                          </p>
                        )}
                      </div>
                      <p className="font-semibold text-[#2D2420]">{formatCurrency(item.total_price)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Total</p>
                  <p className="text-lg font-bold text-[#2D2420]">{formatCurrency(selectedOrder.total_amount)}</p>
                </div>
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Paid</p>
                  <p className="text-lg font-bold text-[#4A7c59]">{formatCurrency(selectedOrder.paid_amount)}</p>
                </div>
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Balance</p>
                  <p className={cn(
                    "text-lg font-bold",
                    selectedOrder.balance_amount > 0 ? "text-[#E76F51]" : "text-[#4A7c59]"
                  )}>
                    {formatCurrency(selectedOrder.balance_amount)}
                  </p>
                </div>
              </div>

              {/* Status Update */}
              {selectedOrder.status !== 'delivered' && (
                <div>
                  <h4 className="font-semibold text-[#2D2420] mb-3">Update Status</h4>
                  <div className="flex gap-2">
                    {['pending', 'packed', 'on_the_way', 'delivered', 'delayed', 'rescheduled'].map((status) => (
                      <Button
                        key={status}
                        variant={selectedOrder.status === status ? 'default' : 'outline'}
                        size="sm"
                        disabled={updatingStatus}
                        onClick={() => handleStatusClick(selectedOrder.id, status)}
                        className={cn(
                          selectedOrder.status === status 
                            ? "bg-[#C97A5E] hover:bg-[#B06850]" 
                            : "border-[#E5E5E5]"
                        )}
                        data-testid={`status-${status}`}
                      >
                        {getStatusLabel(status)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => openBosDownloadModal(selectedOrder)} disabled={downloadingBOS}>
                  Download BOS
                </Button>
                {selectedOrder.gst_invoice_no ? (
                  <Button onClick={() => handleDownloadGstInvoice(selectedOrder)} disabled={downloadingGst}>
                    <FileText className="w-4 h-4 mr-2" />
                    Download GST Invoice
                  </Button>
                ) : (
                  <Button onClick={() => handleGenerateGstInvoice(selectedOrder)} disabled={generatingGst}>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate GST Invoice
                  </Button>
                )}
              </div>

              {/* Payment History */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#2D2420]">Payment History</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openPaymentModal('add')}
                    disabled={customerOutstanding <= 0}
                    className="border-[#E5E5E5]"
                  >
                    Add Payment
                  </Button>
                </div>
                {selectedOrder.payments?.length === 0 ? (
                  <p className="text-center text-[#A8A8A8] py-4">No payments recorded</p>
                ) : (
                  <div className="space-y-2">
                    {selectedOrder.payments?.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-[#2D2420]">
                            {payment.credit_note_no ? 'credit note' : payment.payment_method}
                            {payment.voucher_no ? ` • ${payment.voucher_no}` : ''}
                            {payment.credit_note_no ? ` • ${payment.credit_note_no}` : ''}
                          </p>
                          <p className="text-xs text-[#A8A8A8]">{formatDate(payment.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="font-semibold text-[#4A7c59]">
                            +{formatCurrency((payment.applied_amount ?? payment.amount ?? 0) + (payment.credit_note_amount || 0))}
                          </p>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={() => handleViewVoucherPdf(payment)}
                            disabled={!payment.voucher_id || voucherLoadingId === payment.id}
                            className="h-8 w-8 border-[#E5E5E5]"
                          >
                            {voucherLoadingId === payment.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Detail Modal */}
      <Dialog open={showCustomerDetailModal} onOpenChange={setShowCustomerDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Customer Details</DialogTitle>
          </DialogHeader>
          {customerDetailLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : selectedCustomer && (
            <div className="space-y-6 py-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-[#C97A5E]/10 flex items-center justify-center text-[#C97A5E] text-2xl font-bold">
                  {selectedCustomer.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#2D2420]">{selectedCustomer.name}</h3>
                  <p className="text-[#A8A8A8]">{selectedCustomer.shop_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#5C4033]">
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> {selectedCustomer.phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {selectedCustomer.area}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Total Orders</p>
                  <p className="text-lg font-bold text-[#2D2420]">{formatCurrency(selectedCustomer.total_orders_amount || 0)}</p>
                </div>
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Total Paid</p>
                  <p className="text-lg font-bold text-[#4A7c59]">{formatCurrency(selectedCustomer.total_payments || 0)}</p>
                </div>
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Outstanding</p>
                  <p className={cn(
                    'text-lg font-bold',
                    (selectedCustomer.outstanding_amount || 0) > 0 ? 'text-[#E76F51]' : 'text-[#4A7c59]'
                  )}>
                    {formatCurrency(selectedCustomer.outstanding_amount || 0)}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Recent Orders</h4>
                {selectedCustomer.orders?.length ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedCustomer.orders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                        <div>
                          <p className="font-medium text-[#2D2420]">{order.order_id}</p>
                          <p className="text-xs text-[#A8A8A8]">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#2D2420]">{formatCurrency(order.total_amount || 0)}</p>
                          <Badge className={cn(
                            'text-xs',
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          )}>
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#A8A8A8] py-4">No orders yet</p>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Recent Payments</h4>
                {selectedCustomer.payments?.length ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedCustomer.payments.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                        <div>
                          <p className="font-medium text-[#2D2420]">{payment.payment_method}</p>
                          <p className="text-xs text-[#A8A8A8]">{formatDate(payment.created_at)}</p>
                        </div>
                        <p className="font-semibold text-[#4A7c59]">+{formatCurrency(payment.amount || 0)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#A8A8A8] py-4">No payments yet</p>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Credit Notes</h4>
                {selectedCustomer.credit_notes?.length ? (
                  <div className="space-y-2 max-h-44 overflow-y-auto">
                    {selectedCustomer.credit_notes.map((note) => (
                      <div key={note.id} className="flex items-start justify-between gap-3 p-3 bg-[#FAFAF9] rounded-lg border border-[#EFEAE6]">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#2D2420]">{note.credit_note_no}</p>
                            <Badge className={cn(
                              'text-xs',
                              note.status === 'REDEEMED'
                                ? 'bg-green-100 text-green-800'
                                : note.status === 'POSTED'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            )}>
                              {note.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-[#A8A8A8] mt-1">{formatDate(note.date || note.created_at)}</p>
                          {note.reason ? <p className="text-sm text-[#5C4033] mt-2">{note.reason}</p> : null}
                          {note.redeemed_at ? <p className="text-[11px] text-[#4A7c59] mt-1">Redeemed on {formatDate(note.redeemed_at)}</p> : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#2D2420]">{formatCurrency(note.amount || 0)}</p>
                          {note.credit_note_url ? (
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              onClick={() => window.open(note.credit_note_url, '_blank', 'noopener,noreferrer')}
                              className="h-8 w-8 border-[#E5E5E5]"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#A8A8A8] py-4">No credit notes yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Reason Modal */}
      <Dialog open={showStatusReasonModal} onOpenChange={setShowStatusReasonModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Provide Reason</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#A8A8A8]">
              Please add a reason for marking this order as {getStatusLabel(pendingStatus)}.
            </p>
            <Textarea
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder="Enter reason"
              className="min-h-[120px] border-[#E5E5E5]"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowStatusReasonModal(false)}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmStatusReason}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#A8A8A8]">Payment Option</label>
              <Select value={paymentOption} onValueChange={handlePaymentOptionChange}>
                <SelectTrigger className="w-full border-[#E5E5E5]">
                  <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                  {paymentAction === 'deliver' && (
                    <SelectItem value="no_payment">No Payment</SelectItem>
                  )}
                  <SelectItem value="full">Full Payment</SelectItem>
                  <SelectItem value="current">Current Bill Amount</SelectItem>
                  <SelectItem value="partial">Partial Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs text-[#A8A8A8]">Payment Amount</label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                disabled={paymentOption === 'no_payment' || paymentOption === 'full' || paymentOption === 'current'}
                className="mt-1 border-[#E5E5E5]"
              />
            </div>

            <div>
              <label className="text-xs text-[#A8A8A8]">Credit Note</label>
              <Select value={selectedCreditNoteId} onValueChange={setSelectedCreditNoteId}>
                <SelectTrigger className="w-full border-[#E5E5E5]">
                  <SelectValue placeholder="Select credit note" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No credit note</SelectItem>
                  {redeemableCreditNotes.map((note) => (
                    <SelectItem key={note.id} value={note.id}>
                      {note.credit_note_no} · {formatCurrency(note.amount)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCreditNoteId !== 'none' ? (
                <p className="mt-1 text-xs text-[#5C4033]">
                  Full redemption only. Selected note amount will be applied in full.
                </p>
              ) : null}
            </div>

            <div>
              <label className="text-xs text-[#A8A8A8]">Collection Source</label>
              <Select value={paymentCollectionSource} onValueChange={setPaymentCollectionSource}>
                <SelectTrigger className="w-full border-[#E5E5E5]">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct_company">Received by Company</SelectItem>
                  <SelectItem value="staff_collected">Collected by Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {paymentCollectionSource === 'staff_collected' ? (
              <div>
                <label className="text-xs text-[#A8A8A8]">Collected By Staff</label>
                <Select value={paymentCollectedByStaffId} onValueChange={setPaymentCollectedByStaffId}>
                  <SelectTrigger className="w-full border-[#E5E5E5]">
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div>
              <label className="text-xs text-[#A8A8A8]">Payment Mode</label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="w-full border-[#E5E5E5]">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs text-[#A8A8A8]">Customer Payment Reference</label>
              <Input
                value={paymentCustomerReference}
                onChange={(e) => setPaymentCustomerReference(e.target.value)}
                placeholder="UPI reference / receipt note"
                className="mt-1 border-[#E5E5E5]"
              />
            </div>

            <div>
              <label className="text-xs text-[#A8A8A8]">Customer Collection Notes</label>
              <Textarea
                value={paymentCustomerCollectionNotes}
                onChange={(e) => setPaymentCustomerCollectionNotes(e.target.value)}
                placeholder="Collected by staff / payer details"
                className="min-h-[80px] border-[#E5E5E5]"
              />
            </div>

            <div>
              <label className="text-xs text-[#A8A8A8]">Remarks</label>
              <Textarea
                value={paymentRemarks}
                onChange={(e) => setPaymentRemarks(e.target.value)}
                placeholder="Add remarks"
                className="min-h-[90px] border-[#E5E5E5]"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPaymentModal(false)}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmPayment}
                disabled={paymentProcessing}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {paymentProcessing ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Order Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Create Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <label className="text-xs text-[#A8A8A8]">Customer</label>
              <Select value={selectedCustomerId} onValueChange={handleCustomerSelect}>
                <SelectTrigger className="w-full border-[#E5E5E5]" disabled={customersLoading}>
                  <SelectValue placeholder={customersLoading ? 'Loading customers...' : 'Select customer'} />
                </SelectTrigger>
                <SelectContent>
                  {customersOptions.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} · {customer.shop_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-[#2D2420]">Order Items</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddOrderItem}
                  className="border-[#E5E5E5]"
                >
                  Add Item
                </Button>
              </div>
              {orderItems.map((item, index) => (
                <div key={index} className="rounded-xl border border-[#E5E5E5] p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <Popover
                      open={!!itemVariantSelectorOpen[index]}
                      onOpenChange={(open) => setItemVariantSelectorOpen((prev) => ({ ...prev, [index]: open }))}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between border-[#E5E5E5]"
                          disabled={!selectedCustomerId}
                        >
                          <span className="truncate">{getSelectedVariantLabel(item.variant_id)}</span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[520px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Type product name to search..." />
                          <CommandList>
                            <CommandEmpty>No matching product found.</CommandEmpty>
                            <CommandGroup>
                              {customerPricing.map((variant) => (
                                <CommandItem
                                  key={variant.variant_id}
                                  value={`${variant.master_name} ${variant.pack_size_label} ${variant.effective_price}`}
                                  onSelect={() => {
                                    handleItemChange(index, 'variant_id', variant.variant_id);
                                    setItemVariantSelectorOpen((prev) => ({ ...prev, [index]: false }));
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      item.variant_id === variant.variant_id ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                  <span>{getVariantOptionLabel(variant)}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {orderItems.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveOrderItem(index)}
                        className="text-[#E76F51] hover:bg-[#FAFAF9]"
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#A8A8A8]">Pieces</label>
                      <Input
                        type="number"
                        min="1"
                        value={item.pieces}
                        onChange={(e) => handleItemChange(index, 'pieces', e.target.value)}
                        className="mt-1 border-[#E5E5E5]"
                      />
                      {item.variant_id ? (
                        <p className={cn(
                          'mt-1 text-xs',
                          getAvailableStock(item.variant_id) < (Number(item.pieces) || 0) ? 'text-[#E76F51]' : 'text-[#5C4033]'
                        )}>
                          Available in {salesWarehouseName}: {getAvailableStock(item.variant_id).toFixed(2)}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-col justify-end text-sm text-[#5C4033]">
                      <span>Line Total</span>
                      <span className="font-semibold text-[#2D2420]">
                        {formatCurrency(getVariantPrice(item.variant_id) * (Number(item.pieces) || 0))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#A8A8A8]">Expected Delivery Date</label>
                <Input
                  type="date"
                  value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  className="mt-1 border-[#E5E5E5]"
                />
              </div>
              <div>
                <label className="text-xs text-[#A8A8A8]">Total Amount</label>
                <div className="mt-3 text-xl font-bold text-[#2D2420]">
                  {formatCurrency(calculateOrderTotal())}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A8A8A8]">Notes</label>
              <Textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Add order notes (optional)"
                className="min-h-[100px] border-[#E5E5E5]"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateOrder}
                disabled={creatingOrder}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {creatingOrder ? 'Creating...' : 'Create Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showBosDownloadModal}
        onOpenChange={(open) => {
          setShowBosDownloadModal(open);
          if (!open) {
            setBosDownloadOrder(null);
            setBosRenderMode('customer');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Download Bill of Supply</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-[#E5E5E5] p-3 text-sm text-[#5C4033]">
              <p className="font-medium text-[#2D2420]">{bosDownloadOrder?.order_id || 'Selected Order'}</p>
              <p className="text-[#A8A8A8]">{bosDownloadOrder?.customer_name || ''}</p>
            </div>
            <div className="space-y-3">
              <label className="flex items-start gap-3 rounded-xl border border-[#E5E5E5] p-3 cursor-pointer">
                <input
                  type="radio"
                  name="bos-render-mode"
                  checked={bosRenderMode === 'customer'}
                  onChange={() => setBosRenderMode('customer')}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-[#2D2420]">Customer BOS</p>
                  <p className="text-sm text-[#A8A8A8]">Uses the customer setting. Previous balance stays hidden by default.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 rounded-xl border border-[#E5E5E5] p-3 cursor-pointer">
                <input
                  type="radio"
                  name="bos-render-mode"
                  checked={bosRenderMode === 'internal'}
                  onChange={() => setBosRenderMode('internal')}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-[#2D2420]">Internal BOS</p>
                  <p className="text-sm text-[#A8A8A8]">Includes previous balance for internal reference only.</p>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowBosDownloadModal(false)}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDownloadBill}
                disabled={downloadingBOS}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
              >
                {downloadingBOS ? 'Preparing...' : 'Download'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
